import { FC } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/joy";
import { useTranslation } from "react-i18next";

import {
  aboutMe,
  details,
  educations,
  experience,
  skills,
} from "src/components/organism/constant";
import { CustomTimeLine } from "src/components/atom/CustomTimeLine";

const About: FC = () => {
  const { t } = useTranslation();

  return (
    <Stack spacing={4} py={{ xs: 3, md: 5 }}>
      <SectionHeader title={t("aboutMe")} eyebrow="Profile" />

      <Card
        variant="outlined"
        sx={{
          p: { xs: 2.5, md: 4 },
          bgcolor: "background.surface",
          backdropFilter: "blur(16px)",
          boxShadow: "sm",
        }}
      >
        <Stack spacing={2}>
          <Typography level="h2">
            {t("aboutMe.title.name")}{" "}
            <Box component="span" sx={{ color: "primary.500" }}>
              {t("aboutMe.title.job")}
            </Box>
          </Typography>
          <Typography
            level="body-lg"
            sx={{ color: "text.secondary", maxWidth: 940 }}
          >
            {t(aboutMe)}
          </Typography>
        </Stack>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
          gap: 3,
        }}
      >
        <Card
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.surface" }}
        >
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.4,
            }}
          >
            {details.map((item, index) => (
              <Box key={`${item.title}-${index}`}>
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  {t(item.title)}
                </Typography>
                <Typography level="title-sm" noWrap>
                  {typeof item.value === "number" ? item.value : t(item.value)}
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
          </Box>
        </Card>

        <Card
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.surface" }}
        >
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Skills
          </Typography>
          <Stack spacing={1.8}>
            {skills.map((item, index) => (
              <Stack spacing={0.8} key={`${item.title}-${index}`}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography level="title-sm">{item.title}</Typography>
                  <Chip size="sm" color="primary" variant="soft">
                    {item.value}%
                  </Chip>
                </Stack>
                <LinearProgress
                  determinate
                  value={item.value}
                  color="primary"
                  sx={{ height: 9, borderRadius: 999 }}
                />
              </Stack>
            ))}
          </Stack>
        </Card>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        <Card
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.surface" }}
        >
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t("education")}
          </Typography>
          <CustomTimeLine list={educations} />
        </Card>

        <Card
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, bgcolor: "background.surface" }}
        >
          <Typography level="title-lg" sx={{ mb: 2 }}>
            {t("experience")}
          </Typography>
          <CustomTimeLine list={experience} />
        </Card>
      </Box>
    </Stack>
  );
};

const SectionHeader = ({
  title,
  eyebrow,
}: {
  title: string;
  eyebrow: string;
}) => {
  return (
    <Stack spacing={1}>
      <Chip
        color="primary"
        variant="soft"
        sx={{ width: "fit-content", borderRadius: 999 }}
      >
        {eyebrow}
      </Chip>
      <Typography level="h2">{title}</Typography>
      <Stack spacing={0.6}>
        <Box
          sx={{
            height: 5,
            width: 88,
            borderRadius: 999,
            bgcolor: "primary.500",
          }}
        />
        <Box
          sx={{
            height: 5,
            width: 52,
            borderRadius: 999,
            bgcolor: "primary.300",
          }}
        />
      </Stack>
    </Stack>
  );
};

export default About;
